import type { ReactNode } from "react";
import { StyleSheet, Text, View } from "react-native";
import type { VolumeLandmarkBand, VolumeLandmarkZone } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { LandmarkBar } from "./LandmarkBar";
import { hiddenFromScreenReader } from "./hiddenFromScreenReader";

// The shape one muscle takes wherever it is read against its band: the name
// and one value on a line, the band's bar, then what the caller says under it.
// One accessible node, one sentence. Two callers: Progress's compact coverage
// row and the program simulator's muscle row (#355).

export type CoverageRowShellProps = {
  theme: UiTheme;
  name: string;
  /** The figure on the name's line. */
  value: string;
  /** Beside the value, e.g. a pace glyph. Hidden from the screen reader. */
  valueExtra?: ReactNode;
  /** The sentence the screen reader hears for the whole node. */
  spoken: string;
  /** The bar; absent for a muscle with no published band. */
  bar?: { weeklySets: number; band: VolumeLandmarkBand; zone: VolumeLandmarkZone; here?: boolean };
  /** Every row but the first carries the hairline above it. */
  divider: boolean;
  /** Under the bar, inside the accessible node (caller hides its own text). */
  children?: ReactNode;
  /** Under the node, outside it, so a control there stays pressable. */
  after?: ReactNode;
};

export function CoverageRowShell({
  theme,
  name,
  value,
  valueExtra,
  spoken,
  bar,
  divider,
  children,
  after,
}: CoverageRowShellProps) {
  const styles = makeStyles(theme);
  return (
    <View style={[styles.row, divider ? styles.divider : null]}>
      {/* "image", not the default: RNW gives `accessibilityRole="text"` or none
          no web role, and a node whose children are all aria-hidden could be
          skipped (PG-53, WagmiChartA11yPoints.tsx). */}
      <View style={styles.reading} accessible accessibilityRole="image" accessibilityLabel={spoken}>
        {/* The name keeps its width and wraps; the figure moves under it only
            when the two do not fit side by side. */}
        <View style={styles.top} {...hiddenFromScreenReader()}>
          <Text style={styles.name} numberOfLines={2}>
            {name}
          </Text>
          <View style={styles.values}>
            <Text style={styles.value}>{value}</Text>
            {valueExtra}
          </View>
        </View>
        {bar ? (
          <View {...hiddenFromScreenReader()}>
            <LandmarkBar theme={theme} weeklySets={bar.weeklySets} band={bar.band} zone={bar.zone} here={bar.here} />
          </View>
        ) : null}
        {children}
      </View>
      {after}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    row: { paddingVertical: s.s2, gap: s.s1 },
    reading: { gap: s.s1 },
    divider: { borderTopWidth: 1, borderTopColor: p.border },
    top: { flexDirection: "row", flexWrap: "wrap", alignItems: "baseline", gap: s.s2 },
    name: { flexShrink: 1, maxWidth: "100%", color: p.ink, fontSize: ty.sizes.sm, fontWeight: "600" },
    values: { flexDirection: "row", alignItems: "baseline", gap: s.s2, marginLeft: "auto" },
    value: { fontFamily: ty.fonts.mono, fontVariant: ["tabular-nums"], color: p.ink, fontSize: ty.sizes.md },
  });
}
